import clsx from 'clsx'
import { PropsWithChildren } from 'react'
import useTranslation from 'next-translate/useTranslation'
import CheckIcon from '@ancon/wildcat-ui/shared/icons/check.svg'
import { AccordionProps } from '@ancon/wildcat-ui/web/orderweb/Accordion'

import HeadingText from '../../app/components/HeadingText'
import BodyText from '../../app/components/BodyText'
import Accordion from '../../app/components/Accordion'
import { AppTestIds } from '../../app/constants'

import styles from './ProductConfigurationAccordion.module.scss'

type AccordionSpecificProps = Pick<
  AccordionProps,
  'title' | 'expanded' | 'onToggle' | 'maxHeight'
>

type ProductConfigurationAccordionProps = PropsWithChildren<
  AccordionSpecificProps & {
    subtitle?: string
    additionalInfo?: string
    hasError?: boolean
    /**
     * Stable, locale-independent id of the section (see
     * `ProductConfigureAccordionId`). Suffixed onto the header test id so
     * tests can target a section without depending on its translated title.
     */
    sectionId: string
    /** Shows a Required or Optional badge */
    isRequired?: boolean
    /** E.g. "1/2 selected" */
    counterText?: string
    /** The required choice is made */
    isComplete?: boolean
    /** Draws attention to the section after a failed Add */
    isAttention?: boolean
  }
>

export default function ProductConfigurationAccordion({
  title,
  subtitle,
  additionalInfo,
  hasError,
  children,
  expanded,
  onToggle,
  maxHeight,
  sectionId,
  isRequired,
  counterText,
  isComplete = false,
  isAttention = false,
}: ProductConfigurationAccordionProps) {
  const { t } = useTranslation('common')

  return (
    <Accordion
      containerClassName={clsx(styles.accordion, {
        [styles.attention]: isAttention,
      })}
      contentContainerClassName={styles.accordionContentContainer}
      headerContainerClassName={styles.headerContainer}
      caretClassName={styles.caret}
      title={
        <div
          className={styles.accordionHeader}
          data-testid={
            AppTestIds.ProductConfigurationAccordionHeader + sectionId
          }
          data-product-section={sectionId}
          // Focused after a failed Add, see ProductConfigureForm
          tabIndex={-1}
        >
          <div
            className={styles.title}
            data-testid={`${AppTestIds.ProductConfigurationAccordion}${title}`}
          >
            {typeof title === 'string' ? (
              <HeadingText as="h3">{title}</HeadingText>
            ) : (
              title
            )}
            {isRequired !== undefined && (
              <span
                className={clsx(styles.badge, {
                  [styles.badgeRequired]: isRequired,
                })}
              >
                {t(
                  isRequired
                    ? 'productModal.required'
                    : 'productModal.optional',
                )}
              </span>
            )}
            {counterText && (
              <span
                className={clsx(styles.counter, {
                  [styles.counterComplete]: isComplete,
                })}
              >
                {isComplete && <CheckIcon aria-hidden />}
                {counterText}
              </span>
            )}
          </div>
          {subtitle && (
            <BodyText
              className={clsx(styles.subtitle, {
                [styles.error]: hasError,
              })}
              data-testid={AppTestIds.ProductConfigurationAccordionError}
            >
              <div>{subtitle}</div>
            </BodyText>
          )}
          {additionalInfo && (
            <BodyText color="body-1">{additionalInfo}</BodyText>
          )}
        </div>
      }
      expanded={expanded}
      onToggle={onToggle}
      maxHeight={maxHeight}
    >
      <div
        className={styles.accordionContent}
        data-testid={`${AppTestIds.ProductConfigurationAccordionChildren}${title}`}
      >
        {children}
      </div>
    </Accordion>
  )
}
